import { useRef, useState, useEffect, useCallback } from "react";
import { useLive } from "@/contexts/LiveDataContext";
import { trpc } from "@/lib/trpc";
import { ExternalLink, ChevronUp, ChevronDown, MapPin } from "lucide-react";

const URL_REGEX = /https?:\/\/\S+/gi;

/** Parse feed text into segments: plain text and URLs */
function parseText(text: string): { type: "text" | "url"; value: string }[] {
  const segments: { type: "text" | "url"; value: string }[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const regex = new RegExp(URL_REGEX.source, "gi");
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: "text", value: text.slice(lastIndex, match.index) });
    }
    segments.push({ type: "url", value: match[0] });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    segments.push({ type: "text", value: text.slice(lastIndex) });
  }
  return segments;
}

/** Check if the entire text is just a URL (no surrounding text) */
function isBareUrl(text: string): boolean {
  return /^\s*https?:\/\/\S+\s*$/i.test(text.trim());
}

/** Generate a readable title from a URL slug as immediate fallback */
function slugTitle(url: string): string {
  try {
    const u = new URL(url);
    const parts = u.pathname.split("/").filter(Boolean);
    // Walk backwards through path segments to find a non-numeric slug
    for (let i = parts.length - 1; i >= 0; i--) {
      const seg = parts[i];
      // Skip purely numeric segments (article IDs like /226871)
      if (/^\d+$/.test(seg)) continue;
      // Skip short date-like segments (2026, 07, 08)
      if (/^\d{2,4}$/.test(seg)) continue;
      const words = seg.replace(/[-_]/g, " ").replace(/\d+$/g, "").trim();
      if (words) {
        return words.charAt(0).toUpperCase() + words.slice(1);
      }
    }
    return u.hostname.replace("www.", "");
  } catch {
    return url;
  }
}

/** Full article link card — used when the feed entry is just a URL */
function ArticleLinkCard({ url, title }: { url: string; title?: string | null }) {
  // Only fetch if no title was provided by admin
  const { data } = trpc.election.fetchUrlMeta.useQuery(
    { url },
    { staleTime: 60_000 * 60, retry: 1, enabled: !title }
  );

  const domain = (() => {
    try {
      return new URL(url).hostname.replace("www.", "");
    } catch {
      return "";
    }
  })();

  // Priority: admin-provided title > fetched title > slug fallback
  const displayTitle = title || data?.title || slugTitle(url);

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex min-w-0 flex-1 items-start gap-2.5 rounded-md border border-border bg-muted/50 px-3 py-2.5 transition-colors hover:bg-muted"
    >
      <ExternalLink className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      <div className="min-w-0 flex-1">
        <span className="line-clamp-2 text-sm font-medium leading-snug group-hover:text-primary transition-colors">
          {displayTitle}
        </span>
        <span className="mt-0.5 block text-[11px] text-muted-foreground truncate">
          {domain}
        </span>
      </div>
    </a>
  );
}

/** Inline link within mixed text — renders as a clickable link */
function InlineArticleLink({ url }: { url: string }) {
  const { data } = trpc.election.fetchUrlMeta.useQuery(
    { url },
    { staleTime: 60_000 * 60, retry: 1 }
  );

  const displayTitle = data?.title || slugTitle(url);

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-primary font-medium hover:underline"
    >
      <ExternalLink className="inline h-3 w-3" />
      {displayTitle}
    </a>
  );
}

/** Renders feed text with embedded URLs as clickable article links */
function FeedTextWithLinks({ text }: { text: string }) {
  const segments = parseText(text);
  if (segments.length === 1 && segments[0].type === "text") {
    return <p className="min-w-0 flex-1 text-sm leading-snug break-words">{text}</p>;
  }
  return (
    <p className="min-w-0 flex-1 text-sm leading-snug break-words">
      {segments.map((seg, i) =>
        seg.type === "url" ? (
          <InlineArticleLink key={i} url={seg.value} />
        ) : (
          <span key={i}>{seg.value}</span>
        )
      )}
    </p>
  );
}

/**
 * Latest-updates feed — driven by database-backed live data via useLive() context.
 * New entries flash with a gold highlight animation.
 */
/** Compute relative time string from a feed entry's time/date */
function getRelativeTime(feed: { time: string; date?: string }[]): string {
  if (feed.length === 0) return "";
  const latest = feed[0]; // newest first
  try {
    // Parse the time (e.g. "12:59 am") and date (e.g. "7 Jul 2026")
    const now = new Date();
    const dateStr = latest.date || now.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    const parsed = new Date(`${dateStr} ${latest.time}`);
    if (isNaN(parsed.getTime())) return "";
    const diffMs = now.getTime() - parsed.getTime();
    if (diffMs < 0) return "just now";
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin} min ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return `${Math.floor(diffHr / 24)}d ago`;
  } catch {
    return "";
  }
}

/** Create a fingerprint for a feed entry to detect new items */
function feedFingerprint(entry: { time: string; text: string; date?: string }): string {
  return `${entry.date || ""}_${entry.time}_${entry.text.slice(0, 50)}`;
}

export default function LiveFeed({ onJumpToMap }: { onJumpToMap?: () => void }) {
  const LIVE = useLive();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [relTime, setRelTime] = useState(() => getRelativeTime(LIVE.feed));
  const [newEntryIndices, setNewEntryIndices] = useState<Set<number>>(new Set());
  const prevFingerprintsRef = useRef<Set<string>>(new Set());
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(true);

  // Check scroll position to show/hide gradient indicators
  const updateScrollIndicators = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollUp(el.scrollTop > 10);
    setCanScrollDown(el.scrollTop < el.scrollHeight - el.clientHeight - 10);
  }, []);

  // Update relative time every 30 seconds
  useEffect(() => {
    setRelTime(getRelativeTime(LIVE.feed));
    const interval = setInterval(() => setRelTime(getRelativeTime(LIVE.feed)), 30_000);
    return () => clearInterval(interval);
  }, [LIVE.feed]);

  // Check scroll indicators on mount and feed changes
  useEffect(() => {
    updateScrollIndicators();
  }, [LIVE.feed, updateScrollIndicators]);

  // Detect new feed entries and highlight them
  useEffect(() => {
    const currentFingerprints = new Set(LIVE.feed.map(feedFingerprint));
    const prevFingerprints = prevFingerprintsRef.current;

    // Only flash if we had previous data (not initial load)
    if (prevFingerprints.size > 0) {
      const newIndices = new Set<number>();
      LIVE.feed.forEach((entry, i) => {
        const fp = feedFingerprint(entry);
        if (!prevFingerprints.has(fp)) {
          newIndices.add(i);
        }
      });
      if (newIndices.size > 0) {
        setNewEntryIndices(newIndices);
        // Scroll to top to show new entries
        scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        // Clear animation after it completes
        const timer = setTimeout(() => setNewEntryIndices(new Set()), 2200);
        return () => clearTimeout(timer);
      }
    }

    prevFingerprintsRef.current = currentFingerprints;
  }, [LIVE.feed]);

  const scrollToTop = () => scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  const scrollToBottom = () => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });

  return (
    <div className="border-2 border-foreground bg-card overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b-2 border-foreground px-4 py-2.5">
        <div className="kicker text-primary shrink-0">Latest updates</div>
        {onJumpToMap && (
          <button
            onClick={onJumpToMap}
            className="hidden lg:flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors"
          >
            <MapPin className="h-3 w-3" />
            CLICK HERE FOR LIVE MAP & RESULTS ↓
          </button>
        )}
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={scrollToTop}
            className="flex h-6 w-6 items-center justify-center rounded border border-border bg-muted hover:bg-secondary transition-colors"
            title="Scroll to top"
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={scrollToBottom}
            className="flex h-6 w-6 items-center justify-center rounded border border-border bg-muted hover:bg-secondary transition-colors"
            title="Scroll to bottom"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
          {relTime && (
            <span className="ml-1 text-[11px] text-muted-foreground">Updated {relTime}</span>
          )}
        </div>
      </div>
      {/* Scrollable feed with gradient indicators */}
      <div className="relative">
        {/* Top gradient fade — shows when scrolled down */}
        <div
          className={`pointer-events-none absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-card to-transparent z-10 transition-opacity duration-200 ${canScrollUp ? "opacity-100" : "opacity-0"}`}
        />
        {/* Bottom gradient fade — shows when more content below */}
        <div
          className={`pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-card to-transparent z-10 transition-opacity duration-200 ${canScrollDown ? "opacity-100" : "opacity-0"}`}
        />
        {/* Scroll hint text at bottom */}
        {canScrollDown && (
          <div className="pointer-events-none absolute bottom-1 left-0 right-0 z-20 flex justify-center">
            <span className="rounded-full bg-foreground/80 px-2.5 py-0.5 text-[10px] font-medium text-background uppercase tracking-wider">
              Scroll for more ↓
            </span>
          </div>
        )}
        <div
          ref={scrollRef}
          onScroll={updateScrollIndicators}
          className="max-h-[480px] divide-y divide-border overflow-y-auto overscroll-contain touch-pan-y"
        >
          {LIVE.feed.length === 0 && (
            <p className="px-4 py-4 text-sm text-muted-foreground">
              No updates yet. Results will appear here as they come in.
            </p>
          )}
          {LIVE.feed.map((u, i) => {
            const bare = isBareUrl(u.text);
            const isNew = newEntryIndices.has(i);
            return (
              <div
                key={i}
                className={`flex flex-col gap-1 px-4 py-3 sm:flex-row sm:gap-3 ${isNew ? "animate-feed-flash" : ""}`}
              >
                <span className="flex shrink-0 items-center gap-2 leading-tight sm:w-20 sm:flex-col sm:items-start sm:gap-0">
                  <span className="tnum text-xs font-bold text-primary">{u.time}</span>
                  {u.date && (
                    <span className="tnum text-[10px] uppercase tracking-wide text-muted-foreground">
                      {u.date}
                    </span>
                  )}
                </span>
                {bare ? (
                  <ArticleLinkCard url={u.text.trim()} title={u.linkTitle} />
                ) : (
                  <FeedTextWithLinks text={u.text} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
